/**
 * Filter Button Badge DOM Component
 *
 * Renders the applied-filters count on the header's filter button (the Razor-rendered
 * `[data-native-interactive-site-map-filters-opener]`), styled like the map chip count badge.
 * Hidden while the count is 0.
 * No Redux, no business logic — receives all values as parameters.
 *
 * Structure:
 *   <button ..__filter-btn>
 *     <svg .../>
 *     <span ..__chip-badge ..__filter-badge hidden>2</span>
 *   </button>
 *
 * @module nativeInteractiveSiteMap/components/filterButtonBadge.dom
 */

import fastdom from 'fastdom';

const BADGE_CLASS = 'nhs-c-native-interactive-site-map__chip-badge';
const FILTER_BADGE_CLASS = 'nhs-c-native-interactive-site-map__filter-badge';

/**
 * @param {HTMLElement} buttonElement - the filter button the badge is appended to
 * @param {number} count - initial applied-filters count
 * @returns {{ setCount: Function, remove: Function }}
 */
export default function createFilterButtonBadgeDom(buttonElement, count) {
  const state = { root: null };

  function initializeDom() {
    const badge = document.createElement('span');
    badge.className = `${BADGE_CLASS} ${FILTER_BADGE_CLASS}`;
    badge.setAttribute('aria-hidden', 'true');
    badge.setAttribute('data-qa', 'native_ism_filters_count');
    badge.textContent = count;
    badge.toggleAttribute('hidden', count === 0);
    state.root = badge;
    buttonElement.appendChild(badge);
  }

  initializeDom();

  return {
    setCount(newCount) {
      fastdom.mutate(() => {
        if (!state.root) {
          return;
        }
        state.root.textContent = newCount;
        state.root.toggleAttribute('hidden', newCount === 0);
      });
    },

    remove() {
      const root = state.root;
      state.root = null;
      fastdom.mutate(() => { root?.remove(); });
    },
  };
}
